import classNames from 'classnames';
import { useEffect, useId, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode, RefObject } from 'react';

import { useUiMessages } from '../../messages';
import { DropdownMenu } from '../DropdownMenu';
import { Icon } from '../Icon';
import { MenuItem } from '../MenuItem';
import { Typography } from '../Typography';

import styles from './Select.module.css';

export type SelectSize = 'sm' | 'md';

export interface SelectOption {
  value: string;
  label: ReactNode;
  /** Muted inline text after the label (e.g. `@handle`). */
  supportingText?: ReactNode;
  /** Leading element (icon / avatar / dot). */
  leading?: ReactNode;
  /** Text matched against the search query (defaults to the label when it is a string). */
  searchValue?: string;
  disabled?: boolean;
}

export interface SelectProps {
  options: SelectOption[];
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  label?: ReactNode;
  hideLabel?: boolean;
  hintText?: ReactNode;
  errorMessage?: ReactNode;
  destructive?: boolean;
  placeholder?: string;
  size?: SelectSize;
  required?: boolean;
  disabled?: boolean;
  /**
   * Bare, borderless trigger with no field wrapper (no label / hint / message) — for
   * embedding as an `Input` `prefix` / `leadingAddon` (e.g. a currency or country picker).
   * The menu sizes to its content instead of the trigger.
   */
  inline?: boolean;
  /** Turn the trigger into a type-to-filter combobox input. */
  searchable?: boolean;
  /** Placeholder for the search input (searchable only; defaults to `placeholder`). */
  searchPlaceholder?: string;
  /** Shown when the query matches no option (searchable only). */
  noResultsText?: ReactNode;
  /** Trailing keyboard hint rendered in the search input (e.g. `⌘K`). */
  shortcutHint?: ReactNode;
  /**
   * Creatable mode (searchable only, HS-1633): when the typed text names no option, a last row
   * offers to create it, and choosing that row calls `onCreateOption` with the trimmed text instead
   * of `onChange`. The caller creates the value and then selects it through `value`; the select
   * keeps no record of it. Without this prop the select behaves exactly as before.
   */
  onCreateOption?: (input: string) => void;
  /** The create row's label (defaults to `UiMessages.select.create`, then `Create "{input}"`). */
  createOptionLabel?: (input: string) => ReactNode;
  /**
   * Keep the selected option's `supportingText` out of the trigger. For a picker whose supporting
   * text describes an option only while choosing it — a count of the values a filter field offers,
   * say — and would read as part of the chosen value once the menu closes.
   */
  supportingTextInMenuOnly?: boolean;
  /** Accessible name when no visible `label` is provided. */
  ariaLabel?: string;
  id?: string;
  className?: string;
}

const NONE = -1;

/** The create row's value — not a value any caller can pass, so it never collides with an option. */
const CREATE_VALUE = '\u0000create';

const optionText = (option: SelectOption): string =>
  option.searchValue ?? (typeof option.label === 'string' ? option.label : option.value);

const nextEnabled = (options: SelectOption[], from: number, dir: 1 | -1): number => {
  const { length } = options;
  if (length === 0) return NONE;
  for (let step = 1; step <= length; step += 1) {
    const index = (from + dir * step + length * length) % length;
    if (!options[index]?.disabled) return index;
  }
  return from;
};

/**
 * Single-select combobox: an `Input`-styled trigger that opens a `DropdownMenu` listbox
 * of `MenuItem` options, with full keyboard support (arrows / Home / End / Enter / Escape)
 * following the WAI-ARIA combobox pattern. With `searchable`, the trigger becomes a
 * type-to-filter input (leading search icon + optional `shortcutHint`). `label`, `hintText`,
 * and `errorMessage` are optional.
 *
 * @interim Seeded from the Figma "Select field"; replaceable by the UI Kit.
 */
export function Select({
  options,
  value,
  defaultValue,
  onChange,
  label,
  hideLabel = false,
  hintText,
  errorMessage,
  destructive = false,
  placeholder,
  size = 'md',
  required,
  disabled = false,
  inline = false,
  searchable = false,
  searchPlaceholder,
  noResultsText,
  shortcutHint,
  onCreateOption,
  createOptionLabel,
  supportingTextInMenuOnly = false,
  ariaLabel,
  id,
  className,
}: SelectProps) {
  const messages = useUiMessages();
  const resolvedPlaceholder = placeholder ?? messages.select?.placeholder ?? 'Select…';
  const resolvedNoResultsText = noResultsText ?? messages.select?.noResults ?? 'No results';
  const autoId = useId();
  const rootId = id ?? autoId;
  const labelId = `${rootId}-label`;
  const listId = `${rootId}-list`;
  const messageId = `${rootId}-message`;
  const hasError = destructive || errorMessage != null;
  const message = errorMessage ?? hintText;

  const isControlled = value !== undefined;
  const [internal, setInternal] = useState(defaultValue ?? '');
  const selectedValue = isControlled ? value : internal;
  const selected = options.find((o) => o.value === selectedValue);

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(NONE);
  const [query, setQuery] = useState('');
  // `typing` gates filtering: opening shows every option; the list narrows only once the
  // user edits the query, so the committed selection stays visible on focus.
  const [typing, setTyping] = useState(false);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const anchorRef: RefObject<HTMLElement | null> = searchable ? inputRef : triggerRef;

  const trimmedQuery = query.trim();
  const filteredOptions =
    searchable && typing && trimmedQuery !== ''
      ? options.filter((o) => optionText(o).toLowerCase().includes(trimmedQuery.toLowerCase()))
      : options;
  // Offered only for text that names no option exactly — picking the existing one is the answer.
  const canCreate =
    searchable &&
    onCreateOption != null &&
    typing &&
    trimmedQuery !== '' &&
    !options.some((o) => optionText(o).toLowerCase() === trimmedQuery.toLowerCase());
  const createLabel = (input: string): ReactNode =>
    createOptionLabel?.(input) ?? messages.select?.create?.(input) ?? `Create "${input}"`;
  const visibleOptions: SelectOption[] = canCreate
    ? [
        ...filteredOptions,
        {
          value: CREATE_VALUE,
          label: createLabel(trimmedQuery),
          leading: <Icon name="plus" size={20} aria-hidden />,
        },
      ]
    : filteredOptions;
  const activeInVisible = (index: number) => visibleOptions[index];

  const openMenu = (active: number) => {
    if (disabled) return;
    setActiveIndex(active);
    setOpen(true);
  };

  const closeMenu = () => {
    setOpen(false);
    setActiveIndex(NONE);
    setTyping(false);
    setQuery('');
  };

  const selectOption = (option: SelectOption | undefined) => {
    if (!option || option.disabled) return;
    if (option.value === CREATE_VALUE) onCreateOption?.(trimmedQuery);
    else {
      if (!isControlled) setInternal(option.value);
      onChange?.(option.value);
    }
    setOpen(false);
    setActiveIndex(NONE);
    setTyping(false);
    setQuery('');
    (searchable ? inputRef.current : triggerRef.current)?.focus();
  };

  const selectedVisibleIndex = () => visibleOptions.findIndex((o) => o.value === selectedValue);

  // Keep the active option scrolled into view.
  useEffect(() => {
    if (!open || activeIndex < 0) return;
    const list = listRef.current;
    const item = list?.querySelector<HTMLElement>(`[data-index="${String(activeIndex)}"]`);
    if (typeof item?.scrollIntoView === 'function') item.scrollIntoView({ block: 'nearest' });
  }, [open, activeIndex]);

  const handleKeyDown = (event: KeyboardEvent) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!open)
          openMenu(
            selectedVisibleIndex() >= 0
              ? selectedVisibleIndex()
              : nextEnabled(visibleOptions, NONE, 1),
          );
        else setActiveIndex((i) => nextEnabled(visibleOptions, i, 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (!open)
          openMenu(
            selectedVisibleIndex() >= 0
              ? selectedVisibleIndex()
              : nextEnabled(visibleOptions, 0, -1),
          );
        else setActiveIndex((i) => nextEnabled(visibleOptions, i, -1));
        break;
      case 'Home':
        if (open) {
          event.preventDefault();
          setActiveIndex(nextEnabled(visibleOptions, NONE, 1));
        }
        break;
      case 'End':
        if (open) {
          event.preventDefault();
          setActiveIndex(nextEnabled(visibleOptions, 0, -1));
        }
        break;
      case 'Enter':
        event.preventDefault();
        if (open && activeIndex >= 0) selectOption(activeInVisible(activeIndex));
        else
          openMenu(
            selectedVisibleIndex() >= 0
              ? selectedVisibleIndex()
              : nextEnabled(visibleOptions, NONE, 1),
          );
        break;
      case ' ':
        // A searchable input needs the space character; only the button toggles on Space.
        if (!searchable) {
          event.preventDefault();
          if (open && activeIndex >= 0) selectOption(activeInVisible(activeIndex));
          else openMenu(nextEnabled(visibleOptions, NONE, 1));
        }
        break;
      case 'Escape':
        if (open) {
          event.preventDefault();
          closeMenu();
        }
        break;
      case 'Tab':
        if (open) closeMenu();
        break;
      default:
        break;
    }
  };

  const activeId = open && activeIndex >= 0 ? `${listId}-opt-${String(activeIndex)}` : undefined;

  const menu = (
    <DropdownMenu
      anchorRef={anchorRef}
      open={open}
      onClose={closeMenu}
      matchWidth={!inline}
      placement="bottom-start"
      id={listId}
      listRef={listRef}
      aria-labelledby={label != null ? labelId : undefined}
      aria-label={label == null ? ariaLabel : undefined}
      aria-activedescendant={activeId}
    >
      {visibleOptions.length === 0 ? (
        <div className={styles.noResults}>
          <Typography as="p" variant="text-sm" className={classNames(styles.noResultsText)}>
            {resolvedNoResultsText}
          </Typography>
        </div>
      ) : (
        visibleOptions.map((option, index) => (
          <MenuItem
            key={option.value}
            id={`${listId}-opt-${String(index)}`}
            data-index={index}
            size={size}
            leading={option.leading}
            supportingText={option.supportingText}
            selected={option.value === selectedValue}
            active={index === activeIndex}
            disabled={option.disabled ?? false}
            onSelect={() => {
              selectOption(option);
            }}
            onMouseEnter={() => {
              if (!option.disabled) setActiveIndex(index);
            }}
          >
            {option.label}
          </MenuItem>
        ))
      )}
    </DropdownMenu>
  );

  const trigger = searchable ? (
    <div
      className={classNames(
        styles.trigger,
        styles.searchBox,
        styles[size],
        inline && styles.inline,
        hasError && styles.error,
      )}
    >
      <Icon name="search-lg" size={20} className={classNames(styles.searchIcon)} aria-hidden />
      {selected?.leading != null && !typing && (
        <span className={styles.leading}>{selected.leading}</span>
      )}
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        className={styles.searchInput}
        value={typing ? query : selected != null ? optionText(selected) : ''}
        placeholder={searchPlaceholder ?? resolvedPlaceholder}
        disabled={disabled}
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={activeId}
        aria-labelledby={label != null ? labelId : undefined}
        aria-label={label == null ? ariaLabel : undefined}
        aria-invalid={hasError || undefined}
        aria-describedby={message != null ? messageId : undefined}
        aria-required={required ? true : undefined}
        onFocus={() => {
          if (!open) openMenu(selectedVisibleIndex());
        }}
        onClick={() => {
          if (!open) openMenu(selectedVisibleIndex());
        }}
        onChange={(event) => {
          setQuery(event.target.value);
          setTyping(true);
          setOpen(true);
          setActiveIndex(0);
        }}
        onKeyDown={handleKeyDown}
      />
      {shortcutHint != null && !typing && (
        <kbd className={styles.shortcut} aria-hidden>
          {shortcutHint}
        </kbd>
      )}
    </div>
  ) : (
    <button
      ref={triggerRef}
      type="button"
      role="combobox"
      aria-haspopup="listbox"
      aria-expanded={open}
      aria-controls={open ? listId : undefined}
      aria-activedescendant={activeId}
      aria-labelledby={label != null ? labelId : undefined}
      aria-label={label == null ? ariaLabel : undefined}
      aria-invalid={hasError || undefined}
      aria-describedby={message != null ? messageId : undefined}
      aria-required={required ? true : undefined}
      disabled={disabled}
      className={classNames(
        styles.trigger,
        styles[size],
        inline && styles.inline,
        hasError && styles.error,
      )}
      onClick={() => {
        if (open) closeMenu();
        else
          openMenu(
            selectedVisibleIndex() >= 0
              ? selectedVisibleIndex()
              : nextEnabled(visibleOptions, NONE, 1),
          );
      }}
      onKeyDown={handleKeyDown}
    >
      {selected?.leading != null && <span className={styles.leading}>{selected.leading}</span>}
      <span className={classNames(styles.value, selected == null && styles.placeholder)}>
        {selected != null ? (
          <>
            <Typography as="span" variant="text-md" className={classNames(styles.valueLabel)}>
              {selected.label}
            </Typography>
            {selected.supportingText != null && !inline && !supportingTextInMenuOnly && (
              <Typography
                as="span"
                variant="text-md"
                className={classNames(styles.valueSupporting)}
              >
                {selected.supportingText}
              </Typography>
            )}
          </>
        ) : (
          resolvedPlaceholder
        )}
      </span>
      <Icon name="chevron-down" size={20} className={classNames(styles.chevron)} />
    </button>
  );

  if (inline) {
    return (
      <span className={classNames(styles.inlineRoot, className)}>
        {trigger}
        {menu}
      </span>
    );
  }

  return (
    <div className={classNames(styles.field, className)}>
      {label != null && (
        <span id={labelId} className={classNames(styles.label, hideLabel && styles.srOnly)}>
          <Typography as="span" variant="text-sm" weight="medium">
            {label}
          </Typography>
          {required && (
            <span className={styles.required} aria-hidden="true">
              *
            </span>
          )}
        </span>
      )}

      {trigger}
      {menu}

      {message != null && (
        <Typography
          as="p"
          variant="text-sm"
          id={messageId}
          className={classNames(hasError ? styles.errorText : styles.hint)}
        >
          {message}
        </Typography>
      )}
    </div>
  );
}
